// Matches — the per-player match timeline, ported from js/renderers-matches.js.
//
// needsPlayer:true, not mergeable: main.js's strip holds the player search, and the
// season select is added INTO it via <SubControlsSlot> (the Stats pattern). NOT a d3
// island: the legacy rebuilds plain %-positioned innerHTML (bars, dots, ticks) every
// render with no transitions, so the marks are plain JSX over the pure 04k-a layer
// (matches-view.ts) — the ComparisonModule pattern. Tooltips reuse the shared
// showTooltipAt / hideTooltip (canvas/tooltip.ts).
//
// DOM shape is the legacy's exactly (.matches-wrap > .matches-filters, .stats-empty,
// .matches-chart-wrap), including the hidden-but-present empty/chart blocks, so
// styles.css applies unchanged.
//
// Two sources, one chart body: the snapshot, or on ?live=1 any player's real matches in her
// current league (T2-11, live/matches.ts).

import { startMatchDrag } from "../canvas/match-drag.js";
import { startMatchCardDragOnMove } from "../canvas/menu-drag.js";
import type { EventFocus } from "../live/event-focus.js";
import { matchOfResult } from "../live/match.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainMatches } from "./explain.js";
import { useCallback, useEffect, useMemo, useReducer, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import type { ProtoAppearance, ProtoEvent, ProtoFixture } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { liveShortName } from "./EventMapBody.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { noDataMessage } from "./no-data.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  INCIDENT_TYPES,
  rowDots,
  matchRowLabel,
  matchTooltip,
  appearancesForSeason,
  matchesAxisMax,
  matchesAxisTicks,
  barGeometry,
  dotLeftPct,
  guideLines,
  type MatchGoalsView,
  positionSegments,
  formatRowDate,
} from "./matches-view.js";
import { snapshotPlayersOrNone, type RawPlayerRecord } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { flattenEventsByMatch } from "../data/shot-pass.js";
import type { PlayerId } from "../data/players.js";
import { useMatches } from "../hooks/useMatches.js";
import { useMatchGoals } from "../hooks/useMatchGoals.js";
import { useAllMatchResults } from "../hooks/useMatchResults.js";
import { useShotPassEvents } from "../hooks/useShotPassEvents.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { LivePartBoundary } from "../live/LiveBoundary.js";
import { LiveError } from "../live/LoadState.js";
import { loadMatchesSeason, loadPositionMinutes, matchesSeasons } from "../live/matches.js";
import { careerLookups, careerSeasons, loadPlayerCareer } from "../live/career.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { teamOfModule } from "../live/team.js";
import { TeamMatches } from "./TeamMatches.js";

type Filters = Record<string, boolean>;

/** Centre the shared tooltip on the hovered element (renderers-matches.js wireTooltip). */
function tipHandlers(text: string) {
  return {
    onMouseEnter: (e: ReactMouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      showTooltipAt(text, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },
    onMouseLeave: () => hideTooltip(),
  };
}

export function MatchesModule({ mod, body, playerId, playerName }: ProtoModuleProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  // Read through to mod._matchesFilters every render (not cached in useState) so an
  // undo/preset restore that swaps the object is picked up — same as the legacy.
  let filters = getModState<Filters>(mod, "_matchesFilters");
  if (!filters) {
    filters = {};
    setModState(mod, "_matchesFilters", filters);
  }
  const activeFilters = filters;

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const toggleFilter = (key: string) => {
    activeFilters[key] = !activeFilters[key];
    forceRender();
  };

  // A team shown here (teams and matches, Ben 2026-10-30): its games instead of a player's. The
  // incident chips are a player's (their shots, passes…), so they go with them.
  const team = LIVE ? teamOfModule(mod) : null;
  if (team) {
    return (
      <div className="matches-wrap">
        <TeamMatches mod={mod} team={team} onChange={forceRender} />
      </div>
    );
  }

  // No fallback to a first player here (unlike Stats/Heat Map) — the legacy shows
  // "No player selected." for an unknown name. Live: any player, by id.
  const p = playerName ? snapshotPlayersOrNone()[playerName] : undefined;

  return (
    <div className="matches-wrap">
      <div className="matches-filters">
        {INCIDENT_TYPES.filter((t) => LIVE || !t.liveOnly).map((t) => (
          <button
            key={t.key}
            type="button"
            className={`matches-filter-chip${activeFilters[t.key] ? " active" : ""}`}
            data-key={t.key}
            onClick={() => toggleFilter(t.key)}
          >
            <span className="matches-filter-dot" style={{ background: t.color }}></span>
            {t.label}
          </button>
        ))}
      </div>
      {LIVE && playerId !== null ? (
        <LiveMatchesChart
          mod={mod}
          playerId={playerId}
          shortName={liveShortName({ playerName, playerId })}
          filters={activeFilters}
          onChange={forceRender}
        />
      ) : !LIVE && p && playerName && playerId !== null ? (
        <SnapshotMatchesChart
          mod={mod}
          player={p}
          playerId={playerId}
          playerName={playerName}
          filters={activeFilters}
          onChange={forceRender}
        />
      ) : (
        <>
          {/* The legacy creates the (empty) season wrap before bailing out. */}
          <SubControlsSlot mod={mod}>
            <div className="season-select-wrap" />
          </SubControlsSlot>
          <div className="stats-empty">No player selected.</div>
          <div className="matches-chart-wrap" style={{ display: "none" }}>
            <div className="matches-axis"></div>
            <div className="matches-rows"></div>
          </div>
        </>
      )}
    </div>
  );
}

interface ChartProps {
  mod: HTMLElement;
  player: RawPlayerRecord;
  playerId: PlayerId;
  playerName: string;
  filters: Filters;
  onChange: () => void;
}

/** A fresh player always starts on their own current season, not whatever the previous
 *  player in this module showed (renderers-matches.js:327). Written through to the element
 *  like the legacy, so saveState captures it. Returns the season to show. */
function resolveMatchesSeason(mod: HTMLElement, playerId: PlayerId, seasonCodes: string[], current: string): string {
  if (getModState<PlayerId>(mod, "_selectedSeasonPlayer") !== playerId) {
    setModState(mod, "_selectedSeason", current);
    setModState(mod, "_selectedSeasonPlayer", playerId);
  }
  const selected = getModState<string>(mod, "_selectedSeason");
  return selected && seasonCodes.includes(selected) ? selected : current;
}

function SnapshotMatchesChart({ mod, player: p, playerId, playerName, filters, onChange }: ChartProps) {
  const seasons = p.seasons!;
  const seasonCode = resolveMatchesSeason(mod, playerId, Object.keys(seasons), p.currentSeason!);
  const seasonCodes = useMemo(() => Object.keys(seasons).sort((a, b) => b.localeCompare(a)), [seasons]);

  const allAppearances = useMatches(playerName);
  const appearances = useMemo(
    () => appearancesForSeason(allAppearances, seasonCode, p.currentSeason),
    [allAppearances, seasonCode, p.currentSeason],
  );
  const matchGoals = useMatchGoals();
  const results = useAllMatchResults();
  const detail = useShotPassEvents(playerId);
  const byMatch = useMemo(() => flattenEventsByMatch(detail), [detail]);

  return (
    <MatchesChartBody
      mod={mod}
      seasonCodes={seasonCodes}
      seasonCode={seasonCode}
      onChange={onChange}
      filters={filters}
      shortName={getPlayerShortName(p)}
      appearances={appearances}
      results={results}
      matchGoals={matchGoals}
      shotsByMatch={byMatch.shotsByMatch}
      passesByMatch={byMatch.passesByMatch}
      teamOf={() => p.team}
    />
  );
}

interface LiveChartProps {
  mod: HTMLElement;
  playerId: PlayerId;
  shortName: string;
  filters: Filters;
  onChange: () => void;
}

function LiveMatchesChart({ mod, playerId, shortName, filters, onChange }: LiveChartProps) {
  // Every club season, each in its own league (Ben, 2026-10-26), from her career — the Bio's
  // Career screen's own call, so the two share it.
  const career = useLiveQuery(`career:${playerId}`, () => loadPlayerCareer(liveApi(), playerId))!;
  const leagues = useLiveLeagueChoices();
  const seasons = useMemo(() => {
    const { leagueOf, countries } = careerLookups(leagues);
    return matchesSeasons(careerSeasons(career.rows, leagueOf, countries));
  }, [career, leagues]);
  const seasonCodes = useMemo(() => seasons.map((s) => s.seasonCode), [seasons]);
  const seasonCode = seasonCodes.length ? resolveMatchesSeason(mod, playerId, seasonCodes, seasonCodes[0]!) : null;
  const competitionId = seasons.find((s) => s.seasonCode === seasonCode)?.competitionId ?? null;
  const seasonLabel = useCallback(
    (code: string) => {
      const s = seasons.find((x) => x.seasonCode === code);
      return s ? `${humanizeSeasonCode(code)} · ${s.teamName}` : humanizeSeasonCode(code);
    },
    [seasons],
  );
  if (seasonCode === null || competitionId === null) {
    // No domestic league on record: nothing to pick, nothing to show.
    return (
      <>
        <SubControlsSlot mod={mod}>
          <div className="season-select-wrap" />
        </SubControlsSlot>
        <div className="stats-empty">{noDataMessage("match", shortName, null)}</div>
        <div className="matches-chart-wrap" style={{ display: "none" }}>
          <div className="matches-axis"></div>
          <div className="matches-rows"></div>
        </div>
      </>
    );
  }
  const select = { mod, seasonCodes, seasonCode, seasonLabel, onChange };
  // One season failing to load keeps the season list, so another can be picked (Ben, 2026-10-26).
  return (
    <LivePartBoundary
      key={`${playerId}:${competitionId}:${seasonCode}`}
      mod={mod}
      fallback={(retry) => (
        <>
          <MatchesSeasonSelect {...select} />
          <LiveError onRetry={retry} />
        </>
      )}
    >
      <LiveMatchesSeason {...select} playerId={playerId} competitionId={competitionId} shortName={shortName} filters={filters} />
    </LivePartBoundary>
  );
}

interface SeasonSelectProps {
  mod: HTMLElement;
  /** Newest first. */
  seasonCodes: string[];
  seasonCode: string;
  seasonLabel?: (code: string) => string;
  onChange: () => void;
}

function MatchesSeasonSelect({ mod, seasonCodes, seasonCode, seasonLabel = humanizeSeasonCode, onChange }: SeasonSelectProps) {
  const seasonItems = useMemo<StyledSelectItem[]>(
    () => seasonCodes.map((code) => ({ value: code, label: seasonLabel(code) })),
    [seasonCodes, seasonLabel],
  );
  const onSeasonChange = (next: string) => {
    setModState(mod, "_selectedSeason", next);
    onChange();
  };
  return (
    <SubControlsSlot mod={mod}>
      <StyledSelect
        items={seasonItems}
        value={seasonCode}
        onChange={onSeasonChange}
        wrapClass="season-select-wrap"
        extraClass="season-select"
      />
    </SubControlsSlot>
  );
}

/** One live season: its matches + her minutes by position, then the chart. */
function LiveMatchesSeason({
  mod,
  seasonCodes,
  seasonCode,
  seasonLabel,
  onChange,
  playerId,
  competitionId,
  shortName,
  filters,
}: SeasonSelectProps & { playerId: PlayerId; competitionId: number; shortName: string; filters: Filters }) {
  const season = useLiveQuery(`matches:season:${playerId}:${competitionId}:${seasonCode}`, () =>
    loadMatchesSeason(liveApi(), playerId, competitionId, seasonCode),
  )!;
  // Live: her minutes at each position that season, under the chart (Ben, 2026-10-18 — moved from the Bio).
  const positionMinutes = useLiveQuery(`matches:positions:${playerId}:${competitionId}:${seasonCode}`, () =>
    loadPositionMinutes(liveApi(), playerId, competitionId, seasonCode),
  )!;
  const appearances = useMemo(
    // The snapshot's helper sorts newest first; every live appearance is the shown season's.
    () => appearancesForSeason(season.appearances, seasonCode, seasonCode),
    [season, seasonCode],
  );
  return (
    <MatchesChartBody
      mod={mod}
      seasonCodes={seasonCodes}
      seasonCode={seasonCode}
      seasonLabel={seasonLabel}
      onChange={onChange}
      filters={filters}
      shortName={shortName}
      appearances={appearances}
      results={season.results}
      matchGoals={season.matchGoals}
      shotsByMatch={season.shotsByMatch}
      passesByMatch={season.passesByMatch}
      teamOf={(a) => season.teamByMatch.get(a.matchId) ?? null}
      positionMinutes={positionMinutes}
      focusPlayer={playerId}
      focusCompetition={competitionId}
    />
  );
}

interface BodyProps {
  mod: HTMLElement;
  /** Newest first. */
  seasonCodes: string[];
  seasonCode: string;
  /** Live: "25/26 · Manchester City" — every club season is listed, so each names its club. */
  seasonLabel?: (code: string) => string;
  onChange: () => void;
  filters: Filters;
  shortName: string;
  /** The shown season's, newest first. */
  appearances: ProtoAppearance[];
  results: Record<string, ProtoFixture[]>;
  matchGoals: Record<string, MatchGoalsView>;
  shotsByMatch: Record<string, ProtoEvent[]>;
  passesByMatch: Record<string, ProtoEvent[]>;
  /** Her team's name in that match (the snapshot: her one team; live: by the match). */
  teamOf: (a: ProtoAppearance) => string | null | undefined;
  /** Live: her minutes at each position that season (chips under the chart); none on the snapshot site.
   *  `id` = the position's code, as each match records it. */
  positionMinutes?: readonly { id: string; position: string; minutes: number }[];
  /** Live: the player (and league) a dragged match / position carries (event-focus.ts); none on the
   *  snapshot site. */
  focusPlayer?: PlayerId;
  focusCompetition?: number;
}

function MatchesChartBody({
  mod,
  seasonCodes,
  seasonCode,
  seasonLabel = humanizeSeasonCode,
  onChange,
  filters,
  shortName,
  appearances,
  results,
  matchGoals,
  shotsByMatch,
  passesByMatch,
  teamOf,
  positionMinutes = [],
  focusPlayer,
  focusCompetition,
}: BodyProps) {
  // Live: a position chip clicked — its matches stay bright and the stretch at that position is
  // lit on each bar (Ben, 2026-10-18). Kept on the module (Undo, dashboards); click again to clear.
  const [shownPosition, setShownPosition] = useState<string | null>(() => getModState<string>(mod, "_matchesPosition") ?? null);
  const pickPosition = (id: string) => {
    const next = shownPosition === id ? null : id;
    setShownPosition(next);
    setModState(mod, "_matchesPosition", next ?? undefined);
  };
  const lit = positionMinutes.some((m) => m.id === shownPosition) ? shownPosition : null;
  // Live: drag a match's minutes bar onto one of the player's maps → that map shows only that game
  // (Ben, 2026-10-18: a drag, not a click, so nothing changes that the user didn't mean).
  const dragMatch = (e: ReactPointerEvent<HTMLDivElement>, a: ProtoAppearance, name: string, ha: string) => {
    if (focusPlayer === undefined || focusCompetition === undefined || window.innerWidth <= 768) return;
    // As every row drag: no text selection, and the canvas doesn't start a move or a box-select.
    e.preventDefault();
    e.stopPropagation();
    const match = ha ? `${ha === "H" ? "vs" : "at"} ${name} · ${formatRowDate(a.matchDate)}` : name;
    // A position highlighted (and played in this match): only her minutes there go with it.
    const windows = lit === null ? [] : positionSegments(a).filter((s) => s.position === lit);
    const position = positionMinutes.find((m) => m.id === lit)?.position;
    const label = windows.length
      ? `${match} · ${position ?? lit} ${windows.map((w) => `${Math.round(w.from)}′–${Math.round(w.to)}′`).join(", ")}`
      : match;
    // The match itself goes too (Ben, 2026-10-30): a Shot Map or Pass Map shows the whole match, a
    // team at a time, with this player's chip on. Not with a position's minutes: that's hers alone.
    const fixture = Object.values(results)
      .flat()
      .find((f) => f.matchId === a.matchId);
    const ref = fixture ? matchOfResult(fixture, focusCompetition, seasonCode) : null;
    const team = teamOf(a);
    const focus: EventFocus = {
      kind: "match",
      playerId: focusPlayer,
      competitionId: focusCompetition,
      seasonCode,
      label,
      matches: [{ matchId: a.matchId, ...(windows.length ? { windows: windows.map((w) => ({ from: w.from, to: w.to })) } : {}) }],
      ...(ref && !windows.length ? { match: ref, playerSide: team === ref.away.name ? "away" : "home", playerName: shortName } : {}),
    };
    // The whole game also drags out as a match (Ben, 2026-10-31: as a form box does) — onto empty
    // canvas for a Match, onto a Match to show it — and still narrows the maps as before. With a
    // position highlighted (Ben: "filtered matches are no longer draggable") the maps get only the
    // player's minutes there; a Match still gets the whole game.
    if (ref) startMatchCardDragOnMove(e.nativeEvent, ref, focus);
    else startMatchDrag(e.nativeEvent, mod, focus);
  };
  // Drag "All" → the map shows this season's matches (every minute of them).
  const dragAll = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (focusPlayer === undefined || focusCompetition === undefined || window.innerWidth <= 768) return;
    const matches = appearances.filter((a) => (a.positions || []).length > 0).map((a) => ({ matchId: a.matchId }));
    if (!matches.length) return;
    const minutes = positionMinutes.reduce((t, m) => t + m.minutes, 0);
    const label = `${humanizeSeasonCode(seasonCode)} · ${minutes.toLocaleString()}′ in ${matches.length} ${matches.length === 1 ? "match" : "matches"}`;
    startMatchDrag(e.nativeEvent, mod, { kind: "season", playerId: focusPlayer, competitionId: focusCompetition, seasonCode, label, matches });
  };
  // Drag a "minutes by position" chip → the map shows her events at that position, every match of
  // the season, only her minutes there (Ben, 2026-10-18). A click still highlights it here.
  const dragPosition = (e: ReactPointerEvent<HTMLButtonElement>, m: { id: string; position: string; minutes: number }) => {
    if (focusPlayer === undefined || focusCompetition === undefined || window.innerWidth <= 768) return;
    const matches = appearances.flatMap((a) => {
      const windows = positionSegments(a).filter((s) => s.position === m.id);
      return windows.length ? [{ matchId: a.matchId, windows: windows.map((w) => ({ from: w.from, to: w.to })) }] : [];
    });
    if (!matches.length) return;
    const label = `${m.position} · ${m.minutes.toLocaleString()}′ in ${matches.length} ${matches.length === 1 ? "match" : "matches"}, ${humanizeSeasonCode(seasonCode)}`;
    startMatchDrag(e.nativeEvent, mod, {
      kind: "position",
      playerId: focusPlayer,
      competitionId: focusCompetition,
      seasonCode,
      label,
      matches,
    });
  };
  const seasonSelect = <MatchesSeasonSelect mod={mod} seasonCodes={seasonCodes} seasonCode={seasonCode} seasonLabel={seasonLabel} onChange={onChange} />;

  if (!appearances.length) {
    return (
      <>
        {seasonSelect}
        {/* Ben's one message (Q-012). */}
        <div className="stats-empty">{noDataMessage("match", shortName, seasonCode)}</div>
        <div className="matches-chart-wrap" style={{ display: "none" }}>
          <div className="matches-axis"></div>
          <div className="matches-rows"></div>
        </div>
      </>
    );
  }

  const axisMax = matchesAxisMax(appearances);
  const ticks = matchesAxisTicks(axisMax);
  const guides = guideLines(axisMax);

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainMatches({
            name: shortName,
            season: humanizeSeasonCode(seasonCode),
            games: appearances.length,
            dots: INCIDENT_TYPES.filter((t) => filters[t.key]).map((t) => t.label),
          })}
        />
      )}
      {seasonSelect}
      <div className="stats-empty" style={{ display: "none" }}></div>
      <div className="matches-chart-wrap">
        <div className="matches-axis">
          {ticks.map((t) => (
            <span key={t.value} className={`matches-tick matches-tick-${t.align}`} style={{ left: `${t.leftPct}%` }}>
              {t.value}
            </span>
          ))}
        </div>
        <div className={`matches-rows${lit !== null ? " pos-lit" : ""}`}>
          {appearances.map((a, i) => {
            const { startPct, widthPct } = barGeometry(a, axisMax);
            const key = String(a.matchId);
            const team = teamOf(a);
            const dots = rowDots(
              {
                appearance: a,
                shots: shotsByMatch[key] ?? [],
                passes: passesByMatch[key] ?? [],
                playerTeam: team,
                matchGoals,
              },
              filters,
              i,
            );
            const label = matchRowLabel(results, a.matchId, team, a.matchDate);
            const segments = lit === null ? [] : positionSegments(a);
            return (
              <div
                key={`${a.matchId}-${i}`}
                className={`matches-row${lit !== null && !segments.some((s) => s.position === lit) ? " matches-row-dim" : ""}`}
              >
                <div className="matches-opponent">
                  <span className="matches-opponent-name">{label.name}</span>
                  {label.ha ? <span className="matches-opponent-ha">{label.ha}</span> : null}
                </div>
                <div className="matches-track" {...tipHandlers(matchTooltip(results, a, team))}>
                  <div className="matches-half-line" style={{ left: `${guides.halfPct}%` }}></div>
                  {guides.ftPct != null ? (
                    <div className="matches-ft-line" style={{ left: `${guides.ftPct}%` }}></div>
                  ) : null}
                  <div
                    className={`matches-bar${focusPlayer !== undefined ? " matches-bar-draggable" : ""}`}
                    style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                    onPointerDown={(e) => dragMatch(e, a, label.name, label.ha)}
                  ></div>
                  {segments
                    .filter((s) => s.position === lit)
                    .map((s) => {
                      const left = dotLeftPct(s.from, axisMax);
                      return (
                        <div
                          key={`${s.from}`}
                          className="matches-pos-seg"
                          style={{ left: `${left}%`, width: `${Math.max(0.6, dotLeftPct(s.to, axisMax) - left)}%` }}
                        />
                      );
                    })}
                  {dots.map((d) => (
                    <span
                      key={d.key}
                      className="matches-dot"
                      style={{ left: `${dotLeftPct(d.minute, axisMax)}%`, background: d.color }}
                      {...tipHandlers(d.tooltip)}
                    ></span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {positionMinutes.length > 0 && (
        <div className="matches-positions">
          <div className="matches-positions-label">
            Minutes by position{lit !== null ? " · highlighted in the matches above" : " · click one to see where"}
          </div>
          <div className="bio-positions">
            {/* "All" first (Ben, 2026-10-18): back to every match; dragged, the whole season. */}
            <button
              type="button"
              className={`bio-position-chip matches-pos-chip${lit === null ? " on" : ""}`}
              onClick={() => lit !== null && pickPosition(lit)}
              onPointerDown={(e) => dragAll(e)}
            >
              All <b>{positionMinutes.reduce((t, m) => t + m.minutes, 0).toLocaleString()}′</b>
            </button>
            {positionMinutes.map((m) => (
              <button
                type="button"
                className={`bio-position-chip matches-pos-chip${m.id === lit ? " on" : ""}`}
                key={m.id}
                onClick={() => pickPosition(m.id)}
                onPointerDown={(e) => dragPosition(e, m)}
              >
                {m.position} <b>{m.minutes.toLocaleString()}′</b>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
